/**
 * ChangesetsPage — admin PIM changesets list (PIM Wave P5 Task 7, owner
 * "Option A" dedicated builder). Route `/admin/changesets`, gated
 * `marketplace.attribute.manage` like every other `/admin/catalog/*`
 * surface. Lists draft/published/rolled_back changesets, creates a new
 * draft, and opens `ChangesetDrawer` for lifecycle actions (op-builder,
 * preview, publish, rollback, delete).
 *
 * "New changeset" opens a small title-only creation sheet rather than a
 * native `window.prompt()` — this repo has no `window.prompt`/`window.
 * confirm` precedent anywhere (kit dialogs only, for i18n + `--g-*` styling
 * consistency), so the brief's "prompts for a title" is read as "asks for a
 * title via a UI prompt", not the literal browser API. On create success the
 * sheet closes and `ChangesetDrawer` opens on the new draft immediately.
 *
 * The status chips reuse the kit `FilterToolbar` (server-side `?status=`
 * filter); its `search` box is additionally wired to a CLIENT-side title
 * filter (there is no `q` param on `GET /admin/catalog/changesets` — mirrors
 * `CategoryListPage`'s own client-side search over its drained list, for the
 * same reason: a small admin-curated set, no reason to add a backend param
 * for it).
 *
 * `pim.changesets` feature-flag-off handling: every route 404s
 * `pim.changeset.disabled` when the flag is off. Rather than the generic
 * error state, this renders a dedicated "changesets are disabled" message
 * and hides "New changeset" — the P1 direct-write pages remain the only way
 * to edit the catalog in that mode, so there's nothing actionable to offer
 * here.
 */
import { useMemo, useState, type ReactElement, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { BottomSheet, Button, Field, GPill } from "@garden/ui";
import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";
import { AdminDataTable, AdminPageHeader, FilterToolbar, type DataTableColumn } from "@/features/admin/_shared";
import {
  isChangesetsDisabledError,
  type ChangesetStatusValue,
  type ChangesetSummary,
} from "@/features/admin/changesets/api";
import { useChangesets, useCreateChangeset } from "@/features/admin/changesets/hooks";
import { ChangesetDrawer } from "@/features/admin/changesets/components/ChangesetDrawer";

const STATUS_TONE: Record<string, "neutral" | "primary" | "success"> = {
  draft: "neutral",
  published: "success",
  rolled_back: "primary",
};

const STATUS_CHIPS: readonly ChangesetStatusValue[] = ["draft", "published", "rolled_back"];

// Stable empty-list reference so the `rows` useMemo dependency doesn't change
// identity every render while the query is loading/empty (react-hooks/exhaustive-deps).
const EMPTY_ROWS: ChangesetSummary[] = [];

export function ChangesetsPage(): ReactElement {
  registerAdminI18n();
  const { t } = useTranslation(ADMIN_NS);

  const [statusFilter, setStatusFilter] = useState<ChangesetStatusValue | null>(null);
  const [search, setSearch] = useState("");
  const [openId, setOpenId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");

  const { data, isLoading, isError, error, refetch } = useChangesets(statusFilter);
  const createMutation = useCreateChangeset();

  const disabled = isChangesetsDisabledError(error);

  const allRows = data?.changesets ?? EMPTY_ROWS;
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (q === "") return allRows;
    return allRows.filter((cs) => cs.title.toLowerCase().includes(q));
  }, [allRows, search]);

  const openCreate = (): void => {
    setTitleDraft("");
    setCreating(true);
  };

  const submitCreate = (): void => {
    const title = titleDraft.trim();
    if (title === "") return;
    createMutation.mutate(
      { title },
      {
        onSuccess: (created) => {
          toast.success(t("pim.changesets.create.success"));
          setCreating(false);
          setOpenId(created.id);
        },
        onError: () => toast.error(t("error.generic")),
      },
    );
  };

  const columns: DataTableColumn[] = [
    { key: "title", label: t("pim.changesets.columns.title") },
    { key: "status", label: t("pim.changesets.columns.status") },
    { key: "opCount", label: t("pim.changesets.columns.opCount"), align: "right" },
    { key: "createdAt", label: t("pim.changesets.columns.createdAt") },
    { key: "activity", label: t("pim.changesets.columns.activity") },
  ];

  const renderCell = (cs: ChangesetSummary, key: string): ReactNode => {
    switch (key) {
      case "title":
        return <span className="font-medium">{cs.title}</span>;
      case "status":
        return (
          <GPill tone={STATUS_TONE[cs.status] ?? "neutral"} size="sm">
            {t(`pim.changesets.status.${cs.status}`)}
          </GPill>
        );
      case "opCount":
        return cs.op_count;
      case "createdAt":
        return <span className="text-[var(--g-secondary)]">{new Date(cs.created_at).toLocaleString()}</span>;
      case "activity":
        if (cs.status === "published" && cs.published_at) {
          return (
            <span className="text-[var(--g-secondary)]">
              {t("pim.changesets.columns.publishedAt", {
                date: new Date(cs.published_at).toLocaleString(),
              })}
            </span>
          );
        }
        if (cs.status === "rolled_back" && cs.rolled_back_at) {
          return (
            <span className="text-[var(--g-secondary)]">
              {t("pim.changesets.columns.rolledBackAt", {
                date: new Date(cs.rolled_back_at).toLocaleString(),
              })}
            </span>
          );
        }
        return "—";
      default:
        return "";
    }
  };

  const renderMobileCard = (cs: ChangesetSummary): ReactElement => (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium">{cs.title}</span>
        <GPill tone={STATUS_TONE[cs.status] ?? "neutral"} size="sm">
          {t(`pim.changesets.status.${cs.status}`)}
        </GPill>
      </div>
      <span className="text-xs text-[var(--g-secondary)]">
        {t("pim.changesets.columns.opCount")}: {cs.op_count}
      </span>
      <span className="text-xs text-[var(--g-secondary)]">{new Date(cs.created_at).toLocaleString()}</span>
    </div>
  );

  const isEmpty = !isLoading && !isError && rows.length === 0;

  return (
    <div data-testid="pim-changesets-page" className="flex flex-col gap-4">
      <AdminPageHeader
        title={t("pim.changesets.title")}
        subtitle={t("pim.changesets.subtitle")}
        actions={
          disabled ? undefined : (
            <Button
              variant="primary"
              size="md"
              leading="plus"
              data-testid="pim-changesets-create"
              onClick={openCreate}
              className="min-h-[44px]"
            >
              {t("pim.changesets.createButton")}
            </Button>
          )
        }
      />

      {disabled ? (
        <p
          data-testid="pim-changesets-disabled"
          className="rounded-[var(--g-radius)] border border-[var(--g-hairline)] bg-[var(--g-surface)] px-4 py-8 text-center text-sm text-[var(--g-secondary)]"
        >
          {t("pim.changesets.disabled")}
        </p>
      ) : (
        <>
          <FilterToolbar
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder={t("pim.changesets.searchPlaceholder")}
            searchAriaLabel={t("pim.changesets.searchAriaLabel")}
            chips={STATUS_CHIPS.map((status) => ({ id: status, label: t(`pim.changesets.status.${status}`) }))}
            activeChipId={statusFilter}
            onChipChange={(id) => setStatusFilter(id as ChangesetStatusValue | null)}
            allChipLabel={t("pim.changesets.filter.all")}
            testid="pim-changesets-filter"
          />

          <AdminDataTable<ChangesetSummary>
            columns={columns}
            rows={rows}
            rowKey={(cs) => cs.id}
            renderCell={renderCell}
            renderMobileCard={renderMobileCard}
            onRowActivate={(cs) => setOpenId(cs.id)}
            rowAriaLabel={(cs) => cs.title}
            testid="pim-changesets"
            isLoading={isLoading}
            isError={isError}
            emptyLabel={t("pim.changesets.empty")}
            errorLabel={t("error.loadFailed")}
          />

          {isError ? (
            <div className="flex justify-center">
              <Button
                variant="ghost"
                size="sm"
                data-testid="pim-changesets-retry"
                onClick={() => void refetch()}
                className="min-h-[44px]"
              >
                {t("pim.changesets.retry")}
              </Button>
            </div>
          ) : null}

          {isEmpty ? (
            <div className="flex justify-center">
              <Button
                variant="secondary"
                size="sm"
                leading="plus"
                data-testid="pim-changesets-empty-create"
                onClick={openCreate}
                className="min-h-[44px]"
              >
                {t("pim.changesets.emptyCta")}
              </Button>
            </div>
          ) : null}
        </>
      )}

      <BottomSheet open={creating} onClose={() => setCreating(false)} title={t("pim.changesets.create.title")}>
        <div className="flex flex-col gap-4">
          <Field
            label={t("pim.changesets.create.titleLabel")}
            data-testid="pim-changesets-create-title"
            value={titleDraft}
            onChange={(event) => setTitleDraft(event.target.value)}
            autoFocus
          />
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setCreating(false)}
              disabled={createMutation.isPending}
              className="min-h-[44px]"
            >
              {t("common.cancel")}
            </Button>
            <Button
              variant="primary"
              size="sm"
              data-testid="pim-changesets-create-submit"
              loading={createMutation.isPending}
              disabled={titleDraft.trim() === ""}
              onClick={submitCreate}
              className="min-h-[44px]"
            >
              {t("pim.changesets.create.submit")}
            </Button>
          </div>
        </div>
      </BottomSheet>

      <ChangesetDrawer changesetId={openId} onClose={() => setOpenId(null)} />
    </div>
  );
}

export default ChangesetsPage;
